import { applyMiddleware, combineReducers, compose, createStore, Middleware, Reducer, ReducersMapObject, Store } from 'redux';
import createSagaMiddleware, { SagaMiddleware, type SagaMonitor, Task } from 'redux-saga';
import { cancel, type Effect } from 'redux-saga/effects';
import { thunk } from 'redux-thunk';
import debounce from 'lodash/debounce';

import { IStoreStorage, StoreStoragesMapObject } from './storage/storage';
import { createPersistentStorage, IPersistentStorage, PersistentStorageType } from './storage/utils';
import { createDynamicMiddleware } from './dynamic.middleware';
import { GeneratorResult } from './models';

const PERSIST_DEBOUNCE_TIME = 1000;

export class StoreManager {
    private _store: Store;
    private _reducers: ReducersMapObject;

    private _persistent: { [storageType: string]: IPersistentStorage } = {
        [PersistentStorageType.Local]: createPersistentStorage('pr.store', PersistentStorageType.Local),
        [PersistentStorageType.Session]: createPersistentStorage('pr.store', PersistentStorageType.Session),
    };

    private _storages: { [storageType: string]: StoreStoragesMapObject } = {
        [PersistentStorageType.Local]: {},
        [PersistentStorageType.Session]: {},
    };

    private _sagaMiddlware: SagaMiddleware<Record<string, unknown>>;
    private _sagas: { [featureName: string]: Task };

    // RTK Query's reducer + middleware are attached lazily on first feature use (see enableApi) so RTK/RTKQ/immer
    // stay off the eager boot path; this dynamic middleware is the seam that lets the api middleware be added
    // after createStore (vanilla Redux can't applyMiddleware later).
    private readonly _dynamicMiddleware = createDynamicMiddleware();
    private readonly _addedMiddleware = new Set<Middleware>();
    private _apiEnabled = false;

    constructor() {
        this._reducers = { none: (state = 0) => state };

        const initialReducer = combineReducers(this._reducers);
        const initialState = {};

        const isProduction = process.env['NODE_ENV'] === 'production';

        const sagaMonitor = isProduction
            ? undefined
            : (
                  window as unknown as {
                      ['__SAGA_MONITOR_EXTENSION__']?: SagaMonitor;
                  }
              )['__SAGA_MONITOR_EXTENSION__'];
        this._sagaMiddlware = createSagaMiddleware({ sagaMonitor });
        this._sagas = {};

        let composeEnhancers = compose;
        if (!isProduction) {
            composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
        }
        // thunk is required by RTK Query (its `initiate` thunks back the generated hooks + saga cache reads).
        // The listener middleware (saga-free effects, e.g. savedsearch's auth-change reconcile) and the api
        // middleware (cache lifetime, tag invalidation, refetching) are RTK — kept off this eager chain and added
        // later into _dynamicMiddleware (see addMiddleware / enableApi) when the first feature that uses them
        // loads, so RTK/RTKQ/immer stay out of the eager boot bundle.
        this._store = createStore(
            initialReducer,
            initialState,
            composeEnhancers(applyMiddleware(thunk, this._sagaMiddlware, this._dynamicMiddleware.middleware)),
        );

        this._store.subscribe(debounce(this._onStateChanged.bind(this), PERSIST_DEBOUNCE_TIME));
    }

    get store() {
        return this._store;
    }

    addFeature<S>(featureName: string, reducer?: Reducer<S>, saga?: () => any) {
        if (reducer) {
            this._reducers[featureName] = reducer;
            this._onReducersChanged();
        }

        if (saga) {
            this._sagas[featureName] = this._sagaMiddlware.run(saga);
        }
    }

    /**
     * Attach a middleware to the store after creation (vanilla Redux can't). Idempotent per middleware instance.
     * Lets RTK middleware (the listener middleware, RTK Query) stay off the eager boot chain and be wired lazily
     * when the first feature that needs it loads — see dynamic.middleware.ts.
     */
    addMiddleware(middleware: Middleware) {
        if (this._addedMiddleware.has(middleware)) {
            return;
        }
        this._addedMiddleware.add(middleware);
        this._dynamicMiddleware.addMiddleware(middleware);
    }

    /**
     * Lazily attach the shared RTK Query api (reducer + middleware) on first feature use. Keeps RTK/RTKQ/immer
     * out of the eager boot bundle: the api module is only pulled in (and wired here) when a feature that uses
     * it loads. Idempotent — the first RTKQ feature to load attaches it; later features are no-ops.
     */
    enableApi(api: { reducerPath: string; reducer: Reducer; middleware: Middleware }) {
        if (this._apiEnabled) {
            return;
        }
        this._apiEnabled = true;
        this._reducers[api.reducerPath] = api.reducer;
        this._onReducersChanged();
        this.addMiddleware(api.middleware);
    }

    addStoreStorage<S, SS>(storageType: PersistentStorageType, featureName: string, storage: IStoreStorage<SS, S>) {
        this._storages[storageType][featureName] = storage;
        this._persistent[storageType].hydrate(featureName, storage);
    }

    removeFeature(featureName: string) {
        if (featureName in this._reducers) {
            delete this._reducers[featureName];
            this._onReducersChanged();
        }

        if (featureName in this._storages) {
            delete this._storages[featureName];
        }

        if (featureName in this._sagas) {
            cancel(this._sagas[featureName]);
            delete this._sagas[featureName];
        }
    }

    runSaga<R>(saga: (() => GeneratorResult<R>) | Effect): Task<R> {
        return this._sagaMiddlware.run(
            typeof saga === 'function'
                ? saga
                : function* (): GeneratorResult<R> {
                      return yield saga;
                  },
        );
    }

    private _onReducersChanged() {
        this._store.replaceReducer(combineReducers(this._reducers));
    }

    private _onStateChanged() {
        const state = this._store.getState();

        if (state) {
            this._persistent[PersistentStorageType.Local].persist(state, this._storages[PersistentStorageType.Local]);
            this._persistent[PersistentStorageType.Session].persist(state, this._storages[PersistentStorageType.Session]);
        }
    }
}
